/**
 * ════════════════════════════════════════════════════════════════
 * TenantContext (r7.6) — which tenant this build / host serves, and what that changes in the shell.
 *
 *   Resolution
 *     1. VITE_TENANT_ID at build time (the Capacitor app is built per tenant — step 9) → sent as
 *        `X-Tenant-Id` on every API call (api/client.js) so the gateway scopes the session even when the
 *        WebView's origin is not the tenant subdomain.
 *     2. Otherwise the gateway resolves the tenant from the Host header (`<slug>.showznticketz.com`) —
 *        GET /api/tenants/current answers for whichever host served the page.
 *
 *   Effects (non-platform tenant only)
 *     • theme: tenant.palette → ThemeContext.setTenantPalette (base theme, mode, primary/secondary tokens)
 *     • document: <title> "<Tenant name>", favicon → icon_url, <meta name="theme-color"> → primary,
 *       <html data-tenant="<slug>"> for CSS hooks
 *     • nav/footer/search/marketplace surfaces read `isPlatform` and hide themselves (TopNav, Footer,
 *       MobileBottomTab, HomePage); event discovery is already tenant-scoped server-side.
 *     • "Powered by ShowzNTicketz" is rendered by <PoweredBy /> on every tenant screen (product rule 3,
 *       not configurable).
 *
 *   The platform tenant (`snt`) is the default in every failure mode — a tenant lookup that fails never
 *   blanks the app; it just renders the marketplace.
 * ════════════════════════════════════════════════════════════════
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { tenantPublic } from '@/api/tenants';
import { useTheme } from '@/themes/ThemeContext';
import { applyNativeChrome, rememberPlayUrl } from '@/lib/native';   // r7.6 step 9 (no-ops on the web)

const PLATFORM = 'snt';
const BUILD_TENANT = (import.meta.env.VITE_TENANT_ID || '').toLowerCase().trim() || null;
const CACHE_KEY = 'snt:tenant:current';   // last good answer — first paint on a tenant host is branded, not a flash of SNT

const PLATFORM_TENANT = Object.freeze({
  slug: PLATFORM, name: 'ShowzNTicketz', type: 'marketplace', status: 'active',
  logo_url: null, icon_url: null, splash_url: null, palette: {}, sender_name: null, subdomain: null,
  links: { privacy: null, terms: null, support: null }, features: {}, android_package: null, play_url: null, is_platform: true,
});

const TenantContext = createContext(null);

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { tenant, host, at } = JSON.parse(raw);
    if (!tenant || host !== window.location.host || Date.now() - at > 7 * 86400e3) return null;
    if (BUILD_TENANT && tenant.slug !== BUILD_TENANT) return null;
    return tenant;
  } catch { return null; }
}
function writeCache(tenant) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ tenant, host: window.location.host, at: Date.now() })); } catch { /* ignore */ }
}

function applyDocument(tenant) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (tenant.is_platform) {
    root.removeAttribute('data-tenant');
    return;   // the platform keeps index.html's title / favicon / theme-color
  }
  root.setAttribute('data-tenant', tenant.slug);
  document.title = tenant.name;
  const primary = /^#[0-9a-f]{6}$/i.test(tenant.palette?.primary || '') ? tenant.palette.primary : null;
  if (primary) {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'theme-color'; document.head.appendChild(meta); }
    meta.content = primary;
  }
  if (tenant.icon_url) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = tenant.icon_url;
    let apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
    apple.href = tenant.icon_url;
  }
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(() => readCache() || PLATFORM_TENANT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setTenantPalette } = useTheme();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await tenantPublic.current();
      const t = r?.tenant && r.tenant.slug ? { ...PLATFORM_TENANT, ...r.tenant, is_platform: r.tenant.slug === PLATFORM } : PLATFORM_TENANT;
      if (t.status === 'suspended') {
        // a suspended tenant host renders nothing tenant-branded — the gateway 404s its data anyway
        setTenant({ ...PLATFORM_TENANT, suspended: t.slug });
      } else {
        setTenant(t);
        writeCache(t);
      }
      setError(null);
    } catch (err) {
      setError(err);
      // keep whatever we had (cache or platform) — never blank the app on a tenant lookup failure
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // theme + document side effects
  useEffect(() => {
    applyDocument(tenant);
    setTenantPalette(tenant.is_platform ? null : (tenant.palette || null));
    // native shell: status bar + splash follow the tenant; Play URL remembered for "native update required"
    const secondary = /^#[0-9a-f]{6}$/i.test(tenant.palette?.secondary || '') ? tenant.palette.secondary : '#0A0A0B';
    applyNativeChrome({ background: tenant.is_platform ? '#0A0A0B' : secondary, mode: tenant.palette?.mode === 'light' ? 'light' : 'dark' });
    rememberPlayUrl(tenant.play_url);
  }, [tenant, setTenantPalette]);

  const value = useMemo(() => {
    const isPlatform = !!tenant.is_platform;
    return {
      tenant, loading, error, reload: load,
      slug: tenant.slug,
      isPlatform,
      isTenant: !isPlatform,
      buildTenant: BUILD_TENANT,
      name: tenant.name,
      brand: { name: tenant.name, logo_url: tenant.logo_url, icon_url: tenant.icon_url, splash_url: tenant.splash_url, primary: tenant.palette?.primary || null },
      links: tenant.links || PLATFORM_TENANT.links,
      features: tenant.features || {},
      feature: (key) => !!(tenant.features && tenant.features[key]),
      publicUrl: tenant.subdomain ? `https://${tenant.subdomain}` : null,
      android: { package: tenant.android_package || null, play_url: tenant.play_url || null },
      supportHref: tenant.links?.support || 'mailto:support@showznticketz.com',
    };
  }, [tenant, loading, error, load]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}

/** Safe variant for components that may render outside the provider (tests, error shells). */
export function useTenantOptional() {
  return useContext(TenantContext);
}

export { PLATFORM, PLATFORM_TENANT, BUILD_TENANT };
